// @ts-check
/**
 * Resolves a theme document against the contract into CSS variables.
 * No DOM, no fetch, no module state. The lab and the app both call this.
 */

/**
 * @typedef {"bg" | "fg" | "border"} Channel
 * @typedef {{ kind: "authored" | "fallback" | "default", from?: string }} TokenSource
 * @typedef {{ path: string, message: string }} ThemeProblem
 * @typedef {{ value: string, description?: string }} PaletteEntry
 * @typedef {{
 *   schemaVersion?: number,
 *   id?: string,
 *   label?: string,
 *   palette?: Record<string, PaletteEntry>,
 *   roles?: Record<string, string>,
 *   elements?: Record<string, Record<string, Partial<Record<Channel, string>>>>,
 * }} ThemeDocument
 * @typedef {{
 *   channels: Channel[],
 *   roles: Record<string, { fallback: string | null, description?: string }>,
 *   elements: Record<string, { states: Record<string, { fallback: string | Partial<Record<Channel, string>> | null, indicator?: string }> }>,
 *   defaultTheme: ThemeDocument,
 * }} ThemeContract
 * @typedef {{ kind: "role", name: string, path: string } | { kind: "channel", element: string, state: string, channel: Channel, path: string }} Target
 * @typedef {{ value: string, source: TokenSource }} Hit
 */

/**
 * @param {ThemeProblem[]} list
 * @param {ThemeProblem} problem
 */
function addProblem(list, problem) {
  if (list.some((item) => item.path === problem.path && item.message === problem.message)) return;
  list.push(problem);
}

const CHANNELS = /** @type {Channel[]} */ (["bg", "fg", "border"]);
const BLOCKED = new Set(["__proto__", "constructor", "prototype"]);
const MAX_DEPTH = 10;

/**
 * @param {string} name
 */
function kebab(name) {
  return name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * "tableRow.even.bg" -> "--table-row-even-bg". "nav.rest.bg" -> "--nav-bg".
 * "roles.panelAlt" and "panelAlt" -> "--panel-alt".
 * @param {string} path
 */
export function cssVarName(path) {
  const parts = String(path).split(".").filter((part) => part.length > 0);
  if (parts[0] === "roles" && parts.length === 2) return `--${kebab(parts[1])}`;
  if (parts.length === 1) return `--${kebab(parts[0])}`;
  const [element, state, channel] = parts;
  const bits = [kebab(element)];
  if (state && state !== "rest") bits.push(state);
  if (channel) bits.push(channel);
  return `--${bits.join("-")}`;
}

/**
 * @param {string} text
 */
function normalizeHex(text) {
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(text.trim());
  if (!match) return null;
  const body = match[1].length === 3 ? match[1].replace(/./g, (ch) => ch + ch) : match[1];
  return `#${body.toLowerCase()}`;
}

/**
 * @param {unknown} palette
 * @param {string} key
 */
function readPaletteValue(palette, key) {
  if (BLOCKED.has(key)) return undefined;
  if (!palette || typeof palette !== "object") return undefined;
  if (!Object.hasOwn(palette, key)) return undefined;
  const entry = /** @type {Record<string, unknown>} */ (palette)[key];
  if (!entry || typeof entry !== "object") return undefined;
  if (!Object.hasOwn(entry, "value")) return undefined;
  const value = /** @type {{ value?: unknown }} */ (entry).value;
  return typeof value === "string" ? value : undefined;
}

/**
 * @param {unknown} spec
 * @param {unknown} palette
 * @param {ThemeProblem[]} problems
 * @param {string} path
 * @param {Set<string>} seenRefs
 * @returns {string | null}
 */
function resolveSpec(spec, palette, problems, path, seenRefs) {
  if (typeof spec !== "string" || spec.trim() === "") return null;
  const text = spec.trim();
  if (text.startsWith("@")) {
    const key = text.slice(1);
    if (seenRefs.has(key)) {
      addProblem(problems, { path, message: `palette cycle at "@${key}"` });
      return null;
    }
    const raw = readPaletteValue(palette, key);
    if (raw === undefined) {
      addProblem(problems, { path, message: `unknown palette key "@${key}" at ${path}` });
      return null;
    }
    const next = new Set(seenRefs);
    next.add(key);
    return resolveSpec(raw, palette, problems, path, next);
  }
  const hex = normalizeHex(text);
  if (!hex) {
    addProblem(problems, { path, message: `bad colour ${JSON.stringify(text)} at ${path}` });
    return null;
  }
  return hex;
}

/**
 * @param {string} target
 * @param {Channel} channel
 * @returns {Target | null}
 */
function qualify(target, channel) {
  const parts = target.split(".");
  if (parts[0] === "roles" && parts.length === 2) {
    return { kind: "role", name: parts[1], path: target };
  }
  if (parts.length === 3 && CHANNELS.includes(/** @type {Channel} */ (parts[2]))) {
    return {
      kind: "channel",
      element: parts[0],
      state: parts[1],
      channel: /** @type {Channel} */ (parts[2]),
      path: target,
    };
  }
  if (parts.length === 2) {
    return { kind: "channel", element: parts[0], state: parts[1], channel, path: `${target}.${channel}` };
  }
  return null;
}

/**
 * @param {string | Partial<Record<Channel, string>> | null | undefined} fallback
 * @param {Channel} channel
 * @returns {Target | null}
 */
function fallbackTarget(fallback, channel) {
  if (fallback == null) return null;
  if (typeof fallback === "string") return qualify(fallback, channel);
  if (typeof fallback === "object" && Object.hasOwn(fallback, channel)) {
    const raw = fallback[channel];
    if (typeof raw !== "string") return null;
    return qualify(raw, channel);
  }
  return null;
}

/**
 * @param {ThemeDocument | null | undefined} doc
 * @param {string} element
 * @param {string} state
 * @param {Channel} channel
 */
function readChannel(doc, element, state, channel) {
  const elements = doc?.elements;
  if (!elements || typeof elements !== "object" || !Object.hasOwn(elements, element)) return undefined;
  const states = elements[element];
  if (!states || typeof states !== "object" || !Object.hasOwn(states, state)) return undefined;
  const triple = states[state];
  if (!triple || typeof triple !== "object" || !Object.hasOwn(triple, channel)) return undefined;
  return triple[channel];
}

/**
 * @param {ThemeDocument | null | undefined} doc
 * @param {ThemeContract} contract
 * @param {ThemeProblem[]} problems
 */
function reportUnknown(doc, contract, problems) {
  const elements = doc?.elements;
  if (elements && typeof elements === "object") {
    for (const element of Object.keys(elements)) {
      if (!Object.hasOwn(elements, element) || BLOCKED.has(element)) continue;
      if (!Object.hasOwn(contract.elements, element)) {
        addProblem(problems, { path: element, message: `unknown element "${element}"` });
        continue;
      }
      const states = elements[element];
      if (!states || typeof states !== "object") continue;
      for (const state of Object.keys(states)) {
        if (!Object.hasOwn(states, state) || BLOCKED.has(state)) continue;
        if (!Object.hasOwn(contract.elements[element].states, state)) {
          addProblem(problems, { path: `${element}.${state}`, message: `unknown state "${element}.${state}"` });
        }
      }
    }
  }
  const roles = doc?.roles;
  if (roles && typeof roles === "object") {
    for (const role of Object.keys(roles)) {
      if (!Object.hasOwn(roles, role) || BLOCKED.has(role)) continue;
      if (!Object.hasOwn(contract.roles, role)) {
        addProblem(problems, { path: `roles.${role}`, message: `unknown role "${role}"` });
      }
    }
  }
}

/**
 * @param {Target} target
 * @param {ThemeDocument} doc
 * @param {ThemeContract} contract
 * @param {ThemeProblem[]} problems
 * @param {Set<string>} stack
 * @param {number} depth
 * @param {"authored" | "default"} label
 * @returns {Hit | null}
 */
function resolveTarget(target, doc, contract, problems, stack, depth, label) {
  if (target.kind === "role") return resolveRole(target.name, doc, contract, problems, stack, depth, label);
  return resolveChannel(target.element, target.state, target.channel, doc, contract, problems, stack, depth, label);
}

/**
 * @param {string} name
 * @param {ThemeDocument} doc
 * @param {ThemeContract} contract
 * @param {ThemeProblem[]} problems
 * @param {Set<string>} stack
 * @param {number} depth
 * @param {"authored" | "default"} label
 * @returns {Hit | null}
 */
function resolveRole(name, doc, contract, problems, stack, depth, label) {
  const token = `roles.${name}`;
  if (depth > MAX_DEPTH || stack.has(token)) {
    addProblem(problems, { path: token, message: `fallback cycle at ${token}` });
    return null;
  }
  const next = new Set(stack);
  next.add(token);
  const roles = doc?.roles;
  if (roles && typeof roles === "object" && Object.hasOwn(roles, name)) {
    const value = resolveSpec(roles[name], doc.palette, problems, token, new Set());
    if (value) return { value, source: { kind: label } };
  }
  const fallback = contract.roles[name]?.fallback ?? null;
  if (typeof fallback === "string") {
    const target = qualify(fallback, "bg");
    if (target) {
      const hit = resolveTarget(target, doc, contract, problems, next, depth + 1, label);
      if (hit) return { value: hit.value, source: { kind: "fallback", from: fallback } };
    }
  }
  if (label !== "default") {
    // A fresh stack: the same token on the way into the default theme is the
    // last resort, not a cycle. Cycles inside the default theme still count.
    const hit = resolveRole(name, contract.defaultTheme, contract, problems, new Set(), 0, "default");
    if (hit) return { value: hit.value, source: { kind: "default" } };
  }
  addProblem(problems, { path: token, message: `no value for ${token}` });
  return null;
}

/**
 * @param {string} element
 * @param {string} state
 * @param {Channel} channel
 * @param {ThemeDocument} doc
 * @param {ThemeContract} contract
 * @param {ThemeProblem[]} problems
 * @param {Set<string>} stack
 * @param {number} depth
 * @param {"authored" | "default"} label
 * @returns {Hit | null}
 */
function resolveChannel(element, state, channel, doc, contract, problems, stack, depth, label) {
  const token = `${element}.${state}.${channel}`;
  if (depth > MAX_DEPTH || stack.has(token)) {
    addProblem(problems, { path: token, message: `fallback cycle at ${token}` });
    return null;
  }
  const next = new Set(stack);
  next.add(token);
  const spec = readChannel(doc, element, state, channel);
  if (typeof spec === "string" && spec.trim() !== "") {
    const value = resolveSpec(spec, doc.palette, problems, token, new Set());
    if (value) return { value, source: { kind: label } };
  }
  const stateDef = contract.elements[element]?.states?.[state];
  const target = fallbackTarget(stateDef?.fallback ?? null, channel);
  if (target) {
    const hit = resolveTarget(target, doc, contract, problems, next, depth + 1, label);
    if (hit) return { value: hit.value, source: { kind: "fallback", from: target.path } };
  }
  if (label !== "default") {
    const hit = resolveChannel(element, state, channel, contract.defaultTheme, contract, problems, new Set(), 0, "default");
    if (hit) return { value: hit.value, source: { kind: "default" } };
  }
  addProblem(problems, { path: token, message: `no value for ${token}` });
  return null;
}

/**
 * @param {ThemeDocument | null} theme
 * @param {ThemeContract} contract
 * @returns {{ vars: Record<string, string>, sources: Record<string, TokenSource>, problems: ThemeProblem[] }}
 */
export function resolveTheme(theme, contract) {
  const problems = /** @type {ThemeProblem[]} */ ([]);
  const usingDefault = theme == null;
  const doc = usingDefault ? contract.defaultTheme : theme;
  /** @type {Record<string, string>} */
  const vars = {};
  /** @type {Record<string, TokenSource>} */
  const sources = {};
  const label = usingDefault ? "default" : "authored";
  reportUnknown(doc, contract, problems);

  for (const role of Object.keys(contract.roles)) {
    const hit = resolveRole(role, doc, contract, problems, new Set(), 0, label);
    if (!hit) continue;
    vars[cssVarName(`roles.${role}`)] = hit.value;
    sources[`roles.${role}`] = hit.source;
  }
  for (const element of Object.keys(contract.elements)) {
    const states = contract.elements[element].states;
    for (const state of Object.keys(states)) {
      for (const channel of CHANNELS) {
        const hit = resolveChannel(element, state, channel, doc, contract, problems, new Set(), 0, label);
        const token = `${element}.${state}.${channel}`;
        if (!hit) continue;
        vars[cssVarName(token)] = hit.value;
        sources[token] = hit.source;
      }
    }
  }
  return { vars, sources, problems };
}
